import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Loader2, Star, Eye, EyeOff, ArrowUp, ArrowDown,
  Image as ImageIcon, X, Upload, Building2,
} from "lucide-react";
import { adminGet, adminPost, adminPut, adminDelete, adminUpload } from "../lib/adminApi";
import { photoUrl } from "../lib/api";
import { CATEGORIES, SERVICES, STATUSES, categoryLabel } from "../data/realizations";

const inputCls = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-white/40";

const EMPTY = {
  title: "", category: "maisons", location: "", year: "", status: "Terminé",
  size: "", duration: "", description: "", services: [],
  cover: "", photos_before: [], photos_during: [], photos_after: [], featured: false, is_published: true,
};

function PhotoGroup({ label, field, values, onAdd, onRemove, uploading, single }) {
  return (
    <div>
      <span className="mb-2 block text-xs font-medium text-white/60">{label}</span>
      <div className="flex flex-wrap gap-2">
        {(single ? (values ? [values] : []) : values || []).map((p) => (
          <div key={p} className="relative h-20 w-24 overflow-hidden rounded-lg border border-white/10">
            <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onRemove(field, p)} className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white hover:bg-red-500"><X size={12} /></button>
          </div>
        ))}
        <label className={`grid h-20 w-24 cursor-pointer place-items-center rounded-lg border border-dashed border-white/25 text-white/50 hover:border-white/50 ${uploading ? "opacity-50" : ""}`}>
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          <input type="file" accept="image/*" multiple={!single} className="hidden" disabled={uploading} onChange={(e) => onAdd(field, e.target.files, single)} />
        </label>
      </div>
    </div>
  );
}

function Form({ initial, editId, onDone, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addPhotos = async (field, files, single) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const { path } = await adminUpload("/admin/construction/upload-photo", file);
        if (single) setForm((f) => ({ ...f, [field]: path }));
        else setForm((f) => ({ ...f, [field]: [...(f[field] || []), path] }));
      }
    } catch { toast.error("Échec de l'envoi d'une photo"); }
    finally { setUploading(false); }
  };
  const removePhoto = (field, p) => {
    if (field === "cover") setForm((f) => ({ ...f, cover: "" }));
    else setForm((f) => ({ ...f, [field]: (f[field] || []).filter((x) => x !== p) }));
  };
  const toggleService = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Le titre est obligatoire");
    setSaving(true);
    try {
      if (editId) await adminPut(`/admin/construction/realizations/${editId}`, form);
      else await adminPost("/admin/construction/realizations", form);
      toast.success(editId ? "Réalisation mise à jour" : "Réalisation créée");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} data-testid="realization-form" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 pb-24">
      <div className="mb-4 flex items-start gap-2 rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold-400">
        <span className="mt-0.5">⚠️</span>
        <span>Remplissez le formulaire, ajoutez vos photos, puis <strong>cliquez sur « Créer la réalisation » en bas</strong> pour bien enregistrer. Sans ce clic, rien n'est publié.</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/60">Titre du projet *</span><input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="rz-title" className={inputCls} placeholder="Villa moderne à Kalabancoura" /></label>
        <label className="block"><span className="mb-1 block text-xs text-white/60">Catégorie</span>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="rz-category" className={inputCls}>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-navy">{c.label}</option>)}
          </select>
        </label>
        <label className="block"><span className="mb-1 block text-xs text-white/60">Statut</span>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="rz-status" className={inputCls}>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-navy">{s}</option>)}
          </select>
        </label>
        <label className="block"><span className="mb-1 block text-xs text-white/60">Lieu</span><input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder="Bamako, Kalabancoura" /></label>
        <label className="block"><span className="mb-1 block text-xs text-white/60">Année</span><input value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} placeholder="2025" /></label>
        <label className="block"><span className="mb-1 block text-xs text-white/60">Surface / nombre d'étages</span><input value={form.size} onChange={(e) => set("size", e.target.value)} className={inputCls} placeholder="R+2, 240 m²" /></label>
        <label className="block"><span className="mb-1 block text-xs text-white/60">Durée des travaux</span><input value={form.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} placeholder="8 mois" /></label>
        <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/60">Description</span><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className={inputCls} placeholder="Décrivez le projet, les défis, le résultat…" /></label>
      </div>

      <div className="mt-4">
        <span className="mb-2 block text-xs font-medium text-white/60">Services réalisés</span>
        <div className="flex flex-wrap gap-2">
          {SERVICES.map((s) => (
            <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`rz-service-${s}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${form.services.includes(s) ? "bg-gold text-navy" : "bg-white/10 text-white/60 hover:bg-white/15"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4">
        <PhotoGroup label="Photo principale (couverture)" field="cover" values={form.cover} onAdd={addPhotos} onRemove={removePhoto} uploading={uploading} single />
        <PhotoGroup label="Galerie — Avant" field="photos_before" values={form.photos_before} onAdd={addPhotos} onRemove={removePhoto} uploading={uploading} />
        <PhotoGroup label="Galerie — Pendant" field="photos_during" values={form.photos_during} onAdd={addPhotos} onRemove={removePhoto} uploading={uploading} />
        <PhotoGroup label="Galerie — Après" field="photos_after" values={form.photos_after} onAdd={addPhotos} onRemove={removePhoto} uploading={uploading} />
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="rz-featured" className="h-4 w-4 accent-gold" /> Mettre en vedette (page d'accueil)</label>
        <label className="inline-flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!form.is_published} onChange={(e) => set("is_published", e.target.checked)} data-testid="rz-published" className="h-4 w-4 accent-emerald-500" /> Publier sur le site</label>
      </div>

      <div className="sticky bottom-0 -mx-5 -mb-24 mt-6 flex flex-wrap items-center gap-3 border-t border-white/10 bg-navy/95 px-5 py-4 backdrop-blur">
        <button type="submit" disabled={saving || uploading} data-testid="rz-save" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy hover:bg-gold-400 disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {editId ? "Enregistrer les modifications" : "Créer la réalisation"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:bg-white/5">Annuler</button>
        {uploading && <span className="text-xs text-gold-400">Envoi des photos en cours… patientez avant d'enregistrer.</span>}
      </div>
    </form>
  );
}

export default function AdminRealizations() {
  const [list, setList] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [initial, setInitial] = useState(EMPTY);

  const load = () => adminGet("/admin/construction/realizations").then(setList).catch(() => toast.error("Erreur de chargement"));
  useEffect(() => { load(); }, []);

  const openNew = () => { setInitial(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (p) => { setInitial({ ...EMPTY, ...p }); setEditId(p.id); setShowForm(true); };
  const onDone = () => { setShowForm(false); setEditId(null); load(); };

  const patch = async (p, body) => { await adminPut(`/admin/construction/realizations/${p.id}`, { ...p, ...body }).catch(() => toast.error("Échec")); load(); };
  const remove = async (p) => { if (!window.confirm(`Supprimer « ${p.title} » ?`)) return; await adminDelete(`/admin/construction/realizations/${p.id}`).then(() => { toast.success("Supprimé"); load(); }).catch(() => toast.error("Échec")); };

  const move = async (idx, dir) => {
    const arr = [...list];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setList(arr);
    await adminPost("/admin/construction/realizations/reorder", { ids: arr.map((x) => x.id) }).catch(() => toast.error("Échec du réordonnancement"));
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-white"><Building2 size={18} /> Nos réalisations</h2>
          <p className="mt-1 text-sm text-white/50">Ajoutez vos chantiers avec photos avant/pendant/après. Mettez en vedette vos plus beaux projets pour la page d'accueil.</p>
        </div>
        {!showForm && <button onClick={openNew} data-testid="rz-new" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-white/90"><Plus size={16} /> Nouvelle réalisation</button>}
      </div>

      {showForm && <div className="mt-5"><Form initial={initial} editId={editId} onDone={onDone} onCancel={() => setShowForm(false)} /></div>}

      <div className="mt-6 space-y-3">
        {!list ? <p className="text-white/50">Chargement…</p> : list.length === 0 ? (
          <p className="text-white/50">Aucune réalisation. Cliquez sur « Nouvelle réalisation ».</p>
        ) : list.map((p, idx) => {
          const cover = p.cover || p.photos_after?.[0] || p.photos_during?.[0] || p.photos_before?.[0];
          const nb = (p.photos_before?.length || 0) + (p.photos_during?.length || 0) + (p.photos_after?.length || 0);
          return (
            <div key={p.id} data-testid={`rz-item-${p.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-3">
              <div className="flex flex-col gap-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`rz-up-${p.id}`} className="grid h-6 w-6 place-items-center rounded bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30"><ArrowUp size={13} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} data-testid={`rz-down-${p.id}`} className="grid h-6 w-6 place-items-center rounded bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30"><ArrowDown size={13} /></button>
              </div>
              <div className="h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-white/10">
                {cover ? <img src={photoUrl(cover)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-white/30"><ImageIcon size={18} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-white">{p.title}</span>
                  {p.featured && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold text-navy">Vedette</span>}
                  {!p.is_published && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/60">Masqué</span>}
                </div>
                <div className="mt-0.5 text-xs text-white/50">{categoryLabel(p.category)} · {p.location || "—"} · {p.year || "—"} · {nb} photo{nb > 1 ? "s" : ""}</div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button onClick={() => patch(p, { featured: !p.featured })} data-testid={`rz-toggle-featured-${p.id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs ${p.featured ? "bg-gold/25 text-gold-400" : "bg-white/10 text-white/60 hover:bg-white/20"}`}><Star size={13} /> Vedette</button>
                <button onClick={() => patch(p, { is_published: !p.is_published })} data-testid={`rz-toggle-publish-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/20">{p.is_published ? <><EyeOff size={13} /> Masquer</> : <><Eye size={13} /> Publier</>}</button>
                <button onClick={() => openEdit(p)} data-testid={`rz-edit-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/20"><Pencil size={13} /> Modifier</button>
                <button onClick={() => remove(p)} data-testid={`rz-delete-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-2.5 py-1.5 text-xs text-red-300 hover:bg-red-500/30"><Trash2 size={13} /> Suppr.</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
